import { useRef } from 'react';
import type { ComponentPropsWithRef } from 'react';

import { Button, type ButtonProps, Text } from '@frontend/components';

export type UploadCategory = 'RFQ_FILE' | 'VENDOR_QUOTE' | 'SUPPORTING_FILE';

export type UploadFilesPayload =
  | File
  | File[]
  | {
      category: UploadCategory;
      file: File | File[];
    };

export type UploadButtonSharedProps = {
  accept?: ComponentPropsWithRef<'input'>['accept'];
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  multiple?: boolean;
  category?: UploadCategory;
  onUpload: (files: UploadFilesPayload) => void;
};

export type UploadButtonProps = (
  | ({ unstyled?: false } & ButtonProps)
  | ({ unstyled: true } & ComponentPropsWithRef<'button'>)
) &
  UploadButtonSharedProps;

const UploadButton = ({
  accept,
  children,
  multiple,
  onClick = () => 0,
  onUpload,
  category,
  ...props
}: UploadButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = () => {
    if (!fileInputRef.current) return;

    const files = [...(fileInputRef.current?.files ?? [])];
    // Reset file input value
    fileInputRef.current.value = '';

    const filesToUpload = multiple ? files : files[0];
    const uploadParams = category
      ? { category, file: filesToUpload }
      : filesToUpload;
    onUpload(uploadParams);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick(e);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  return (
    <Text as="span" width="100%" display="block" data-testid="upload-button">
      <input
        accept={accept}
        style={{ display: 'none' }}
        type="file"
        multiple={multiple}
        ref={fileInputRef}
        onChange={handleChange}
      />
      {props.unstyled ? (
        <button onClick={handleClick} {...props}>
          {children}
        </button>
      ) : (
        <Button onClick={handleClick} {...props}>
          {children}
        </Button>
      )}
    </Text>
  );
};
UploadButton.displayName = 'UploadButton';

export default UploadButton;
